'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCookie } from 'cookies-next';

interface UserData {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  userRole: string;
  userFullName: string;
  companyId: string;
  companyUserId: string;
  avatar?: string;
}

interface UserContextType {
  user: UserData | null;
  isLoading: boolean;
  refreshUser: () => void;
  updateUserAvatar: (avatar: string) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUserFromCookies = () => {
    try {
      const firstName = getCookie('first_name') as string || '';
      const lastName = getCookie('last_name') as string || '';
      const middleName = getCookie('middle_name') as string || '';
      const email = getCookie('email') as string || '';
      const userRole = getCookie('user_role') as string || '';
      const userFullName = getCookie('user_full_name') as string || '';
      const companyId = getCookie('company_id') as string || '';
      const companyUserId = getCookie('company_user_id') as string || '';
      // Load profile picture from authenticate endpoint (stored in localStorage during login)
      const avatar = localStorage.getItem('user_avatar') || getCookie('user_avatar') as string || '';

      // Check if user is authenticated
      const isAuthenticated = getCookie('isAuthenticated') === 'true';
      const token = getCookie('token');

      // Only set user if we have authentication and at least some basic information
      if (isAuthenticated && token && (firstName || lastName || userFullName)) {
        setUser({
          firstName,
          lastName,
          middleName,
          email,
          userRole,
          userFullName,
          companyId,
          companyUserId,
          avatar,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user from cookies:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = () => {
    loadUserFromCookies();
  };

  const updateUserAvatar = (avatar: string) => {
    if (user) {
      setUser({ ...user, avatar });
    }
  };

  useEffect(() => {
    loadUserFromCookies();
  }, []);

  // Listen for storage events to sync user data across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const currentUserId = getCookie('ca_user_id');
      
      // Handle logout events
      if (e.key === `ca-logout-${currentUserId}`) {
        console.log('UserContext: Received logout event for current user, clearing user data...');
        setUser(null);
        setIsLoading(false);
      }
      
      // Handle login events - close tab if another tab logs in with same user
      if (e.key === `ca-login-${currentUserId}`) {
        // Check if this tab just logged in (should stay open)
        const justLoggedIn = sessionStorage.getItem('just-logged-in');
        if (justLoggedIn === 'true') {
          console.log('UserContext: This tab just logged in, keeping it open');
          // Clear the flag since we've handled it
          sessionStorage.removeItem('just-logged-in');
        } else {
          console.log('UserContext: Another tab logged in with same user, closing this tab...');
          // Close the current tab
          window.close();
          // If window.close() doesn't work (some browsers block it), redirect to login
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    };

    // Listen for logout and login events from other tabs (only for current user)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value: UserContextType = {
    user,
    isLoading,
    refreshUser,
    updateUserAvatar,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
